import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Ticket, 
  Wrench, 
  Calendar, 
  FileText, 
  Printer,
  CalendarClock,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Filter
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ClientTimeline Component
 * Mostra una timeline cronologica unificata di tutte le attività di un cliente
 */
export default function ClientTimeline({ clienteId, clienteName }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const fetchTimeline = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all related data in parallel
      const [ticketsRes, interventiRes, appuntamentiRes, documentiRes, abbonamentiRes] = await Promise.all([
        axios.get(`${API}/tickets?cliente_id=${clienteId}`),
        axios.get(`${API}/interventi?cliente_id=${clienteId}`),
        axios.get(`${API}/appuntamenti?cliente_id=${clienteId}`),
        axios.get(`${API}/documenti?cliente_id=${clienteId}`),
        axios.get(`${API}/abbonamenti?cliente_id=${clienteId}`)
      ]);

      // Transform into unified timeline events
      const timelineEvents = [];

      // Tickets
      ticketsRes.data.forEach(t => {
        timelineEvents.push({
          id: `ticket-${t.id}`,
          type: 'ticket',
          icon: Ticket,
          title: t.oggetto,
          subtitle: `Ticket #${t.numero}`,
          date: t.created_at,
          status: t.stato,
          priority: t.priorita,
          link: `/tickets/${t.id}`,
          color: 'blue',
          details: t.descrizione
        });
      });

      // Interventi
      interventiRes.data.forEach(i => {
        timelineEvents.push({
          id: `intervento-${i.id}`,
          type: 'intervento',
          icon: Wrench,
          title: i.descrizione || 'Intervento tecnico',
          subtitle: `Intervento #${i.numero || i.id.substring(0,6)}`,
          date: i.data_intervento || i.created_at,
          status: i.stato,
          color: 'emerald',
          details: i.note,
          extra: i.costo_totale ? `€ ${i.costo_totale.toFixed(2)}` : null
        });
      });

      // Appuntamenti
      appuntamentiRes.data.forEach(a => {
        const apptDate = a.data_ora ? new Date(a.data_ora) : null;
        const isValidDate = apptDate && !isNaN(apptDate.getTime());
        const isPast = isValidDate ? apptDate < new Date() : false;
        timelineEvents.push({
          id: `appuntamento-${a.id}`,
          type: 'appuntamento',
          icon: Calendar,
          title: a.titolo || 'Appuntamento',
          subtitle: isValidDate
            ? apptDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
            : '—',
          date: a.data_ora,
          status: isPast ? 'completato' : 'programmato',
          color: 'purple',
          details: a.note
        });
      });

      // Documenti
      documentiRes.data.forEach(d => {
        timelineEvents.push({
          id: `documento-${d.id}`,
          type: 'documento',
          icon: FileText,
          title: d.oggetto || `Documento ${d.tipo}`,
          subtitle: `${d.tipo?.toUpperCase()} #${d.numero}`,
          date: d.created_at,
          status: d.stato,
          color: 'amber',
          extra: d.importo_totale ? `€ ${d.importo_totale.toFixed(2)}` : null
        });
      });

      // Abbonamenti (eventi di creazione/rinnovo)
      abbonamentiRes.data.forEach(a => {
        timelineEvents.push({
          id: `abbonamento-${a.id}`,
          type: 'abbonamento',
          icon: CalendarClock,
          title: a.descrizione || 'Abbonamento',
          subtitle: `Scade: ${formatDate(a.data_scadenza)}`,
          date: a.data_inizio || a.created_at,
          status: a.attivo ? 'attivo' : 'scaduto',
          color: 'cyan',
          extra: a.importo ? `€ ${a.importo.toFixed(2)}/anno` : null
        });
      });

      // Sort by date descending (most recent first) — sicuro contro date invalide
      timelineEvents.sort((a, b) => {
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        return (isNaN(db) ? 0 : db) - (isNaN(da) ? 0 : da);
      });

      setEvents(timelineEvents);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    if (clienteId) {
      fetchTimeline();
    }
  }, [clienteId, fetchTimeline]);

  // Filter events
  const filteredEvents = events.filter(e => {
    if (activeFilter === 'all') return true;
    return e.type === activeFilter;
  });

  // Limit displayed events unless showAll
  const displayedEvents = showAll ? filteredEvents : filteredEvents.slice(0, 10);

  const getStatusBadge = (event) => {
    const statusColors = {
      'aperto': 'bg-blue-500/20 text-blue-600 border-blue-500/30',
      'in_lavorazione': 'bg-amber-500/20 text-amber-600 border-amber-500/30',
      'chiuso': 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
      'completato': 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
      'annullato': 'bg-gray-500/20 text-gray-600 border-gray-500/30',
      'programmato': 'bg-purple-500/20 text-purple-600 border-purple-500/30',
      'attivo': 'bg-cyan-500/20 text-cyan-600 border-cyan-500/30',
      'scaduto': 'bg-red-500/20 text-red-600 border-red-500/30',
      'pagato': 'bg-emerald-500/20 text-emerald-600 border-emerald-500/30',
      'non_pagato': 'bg-red-500/20 text-red-600 border-red-500/30',
    };

    const color = statusColors[event.status] || 'bg-gray-500/20 text-gray-600 border-gray-500/30';
    
    return (
      <Badge variant="outline" className={cn("text-[10px] rounded px-1.5", color)}>
        {event.status?.replace('_', ' ')}
      </Badge>
    );
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-500' },
      emerald: { bg: 'bg-emerald-500', border: 'border-emerald-500', text: 'text-emerald-500' },
      purple: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-500' },
      amber: { bg: 'bg-amber-500', border: 'border-amber-500', text: 'text-amber-500' },
      cyan: { bg: 'bg-cyan-500', border: 'border-cyan-500', text: 'text-cyan-500' },
    };
    return colors[color] || colors.blue;
  };

  const filterCounts = {
    all: events.length,
    ticket: events.filter(e => e.type === 'ticket').length,
    intervento: events.filter(e => e.type === 'intervento').length,
    appuntamento: events.filter(e => e.type === 'appuntamento').length,
    documento: events.filter(e => e.type === 'documento').length,
    abbonamento: events.filter(e => e.type === 'abbonamento').length,
  };

  if (loading) {
    return (
      <Card className="rounded-md">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Clock className="h-4 w-4 animate-pulse" />
            Caricamento Timeline...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="rounded-md" data-testid="client-timeline">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Clock className="h-4 w-4 text-cyan-500" />
            Timeline Attività ({events.length})
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setExpanded(!expanded)}
            className="h-8 w-8 p-0 rounded-md"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0">
          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-1 mb-4 pb-3 border-b border-border">
            {[
              { key: 'all', label: 'Tutto', icon: Filter },
              { key: 'ticket', label: 'Tickets', icon: Ticket },
              { key: 'intervento', label: 'Interventi', icon: Wrench },
              { key: 'appuntamento', label: 'Appuntamenti', icon: Calendar },
              { key: 'documento', label: 'Documenti', icon: FileText },
              { key: 'abbonamento', label: 'Abbonamenti', icon: CalendarClock },
            ].map(({ key, label, icon: Icon }) => (
              <Button
                key={key}
                variant={activeFilter === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(key)}
                className={cn(
                  "h-7 text-xs rounded-md gap-1",
                  activeFilter === key && "bg-cyan-500 hover:bg-cyan-600"
                )}
                data-testid={`timeline-filter-${key}`}
              >
                <Icon className="h-3 w-3" />
                <span className="hidden sm:inline">{label}</span>
                <Badge variant="secondary" className="ml-1 h-4 min-w-4 p-0 justify-center rounded text-[10px]">
                  {filterCounts[key]}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Timeline */}
          {filteredEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Nessuna attività trovata</p>
            </div>
          ) : (
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />

              {/* Events */}
              <div className="space-y-4">
                {displayedEvents.map((event, index) => {
                  const Icon = event.icon;
                  const colorClasses = getColorClasses(event.color);

                  return (
                    <div 
                      key={event.id} 
                      className="relative pl-10 group"
                      data-testid={`timeline-event-${event.id}`}
                    >
                      {/* Icon circle */}
                      <div className={cn(
                        "absolute left-0 top-0 w-7 h-7 rounded-full flex items-center justify-center",
                        colorClasses.bg, "text-white shadow-md"
                      )}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>

                      {/* Content */}
                      <div className={cn(
                        "bg-card border rounded-md p-3 transition-all",
                        "hover:shadow-md hover:border-cyan-500/30",
                        colorClasses.border, "border-l-2"
                      )}>
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex-1 min-w-0">
                            {event.link ? (
                              <Link 
                                to={event.link} 
                                className={cn("font-medium text-sm hover:underline", colorClasses.text)}
                              >
                                {event.title}
                              </Link>
                            ) : (
                              <span className="font-medium text-sm">{event.title}</span>
                            )}
                            <p className="text-xs text-muted-foreground">{event.subtitle}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {event.extra && (
                              <span className="text-xs font-mono font-medium text-emerald-600">
                                {event.extra}
                              </span>
                            )}
                            {getStatusBadge(event)}
                          </div>
                        </div>

                        {/* Details */}
                        {event.details && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {event.details}
                          </p>
                        )}

                        {/* Date */}
                        <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground font-mono">
                          <Clock className="h-3 w-3" />
                          {(() => {
                            if (!event.date) return '—';
                            const d = new Date(event.date);
                            if (isNaN(d.getTime())) return '—';
                            return d.toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Show more button */}
              {filteredEvents.length > 10 && (
                <div className="mt-4 text-center">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowAll(!showAll)}
                    className="rounded-md text-xs"
                  >
                    {showAll ? (
                      <>
                        <ChevronUp className="h-3 w-3 mr-1" />
                        Mostra meno
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Mostra altri {filteredEvents.length - 10} eventi
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
